import { FC, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Spinner,
  Typography,
  IconButton,
  Button,
  Input,
  Textarea,
  Switch,
  Card,
  CardBody,
  Chip,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from '@material-tailwind/react'
import { AiOutlinePlus } from 'react-icons/ai'
import { PiTrash } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import { usePageTitle } from '../hooks/toolkit'
import { useFeatureFlag } from '../hooks/authorization'
import { useDarkMode } from '../contexts/DarkModeContext'
import { FEATURE_FLAGS } from '../utils/constants/features.constants'
import {
  getLandingPageCodes,
  createLandingPageCode,
  updateLandingPageCode,
  deleteLandingPageCode,
  DUPLICATE_LANDING_CODE,
} from '../utils/helpers/supabase.helpers'
import { useGeneralToast } from '../hooks/toast'
import { formatDateString } from '../utils/helpers/dates.helpers'
import ResponsiveDialog from '../components/ResponsiveDialog'
import NoUploads from '../components/NoUploads'
import { LandingPageCode } from '../types/supabase'

// Codes go into a URL query parameter as-is (no percent-encoding), so we limit
// them to the RFC 3986 unreserved set plus a couple of safe punctuation chars.
const CODE_PATTERN = /^[A-Za-z0-9._~-]+$/

const isValidUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false
    if (!parsed.hostname) return false
    if (parsed.hostname === 'localhost') return true
    // Hostname must look like a real domain: at least two labels, all non-empty,
    // and a TLD that's at least 2 chars. Rejects things like "google.", ".com",
    // "google", "google.c".
    const labels = parsed.hostname.split('.')
    if (labels.length < 2) return false
    if (labels.some((label) => label.length === 0)) return false
    const tld = labels[labels.length - 1] ?? ''
    return tld.length >= 2
  } catch {
    return false
  }
}

const extractErrorMessage = (error: unknown, t: (k: string) => string): string => {
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = String((error as { message?: unknown }).message ?? '')
    if (msg === DUPLICATE_LANDING_CODE) return t('dashboardLandingPages.errors.codeTaken')
    return msg || JSON.stringify(error)
  }
  return JSON.stringify(error)
}

type FormState = {
  code: string
  url: string
  notes: string
  isDefault: boolean
}

const emptyForm: FormState = { code: '', url: '', notes: '', isDefault: false }

interface LandingPageFormProps {
  initial: FormState
  saving: boolean
  onSubmit: (form: FormState) => Promise<void>
  onCancel: () => void
}

const LandingPageForm: FC<LandingPageFormProps> = ({ initial, saving, onSubmit, onCancel }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const [form, setForm] = useState<FormState>(initial)
  const [codeErr, setCodeErr] = useState(false)
  const [urlErr, setUrlErr] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const code = form.code.trim()
    const url = form.url.trim()
    const codeInvalid = !CODE_PATTERN.test(code)
    const urlInvalid = !isValidUrl(url)
    setCodeErr(codeInvalid)
    setUrlErr(urlInvalid)
    if (codeInvalid || urlInvalid) return
    await onSubmit({ code, url, notes: form.notes.trim(), isDefault: form.isDefault })
  }

  const labelProps = {
    className: 'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
  }

  return (
    <form className="flex flex-col gap-4 px-2" onSubmit={handleSubmit} noValidate>
      <div className="flex flex-col">
        <Input
          value={form.code}
          onChange={(e) => {
            const { value } = e.currentTarget
            setCodeErr(false)
            setForm((f) => ({ ...f, code: value }))
          }}
          label={t('dashboardLandingPages.fields.code')}
          error={codeErr}
          disabled={saving}
          labelProps={labelProps}
          data-testid="landing-page-code-input"
          {...(darkMode && { color: 'white' as colors })}
        />
        {codeErr && (
          <Typography variant="small" color="red" data-testid="landing-page-code-error">
            {t('dashboardLandingPages.errors.invalidCode')}
          </Typography>
        )}
      </div>

      <div className="flex flex-col">
        <Input
          value={form.url}
          onChange={(e) => {
            const { value } = e.currentTarget
            setUrlErr(false)
            setForm((f) => ({ ...f, url: value }))
          }}
          type="url"
          label={t('dashboardLandingPages.fields.url')}
          error={urlErr}
          disabled={saving}
          labelProps={labelProps}
          data-testid="landing-page-url-input"
          {...(darkMode && { color: 'white' as colors })}
        />
        {urlErr && (
          <Typography variant="small" color="red">
            {t('dashboardLandingPages.errors.invalidUrl')}
          </Typography>
        )}
      </div>

      <div className="flex flex-col">
        <Textarea
          className="w-full -mb-2 text-primary-600 focus:text-primary-700 dark:!text-base-gray-500 dark:focus:text-base-gray-400"
          rows={2}
          value={form.notes}
          onChange={(e) => {
            const { value } = e.currentTarget
            setForm((f) => ({ ...f, notes: value }))
          }}
          label={t('dashboardLandingPages.fields.notes')}
          disabled={saving}
          labelProps={labelProps}
          {...(darkMode && { color: 'blue-gray' as colors })}
        />
      </div>

      <div className="flex flex-col">
        <Switch
          id="landing-page-is-default"
          color={(darkMode ? 'accent' : 'primary') as colors}
          label={t('dashboardLandingPages.fields.isDefault')}
          checked={form.isDefault}
          onChange={(e) => {
            const { checked } = e.currentTarget
            setForm((f) => ({ ...f, isDefault: checked }))
          }}
          disabled={saving}
          data-testid="landing-page-is-default-switch"
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button size="sm" variant="outlined" onClick={onCancel} disabled={saving}>
          {t('dashboardLandingPages.cancel')}
        </Button>
        <Button type="submit" size="sm" disabled={saving} data-testid="landing-page-save-btn">
          {saving ? <Spinner className="mx-auto h-4 w-4" /> : t('dashboardLandingPages.save')}
        </Button>
      </div>
    </form>
  )
}

const DashboardLandingPages: FC = () => {
  const { t } = useTranslation()
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const flagOn = useFeatureFlag(FEATURE_FLAGS.LANDING_PAGES)

  usePageTitle(t('dashboardLandingPages.pageTitle'))

  const [loading, setLoading] = useState<boolean>(true)
  const [rows, setRows] = useState<LandingPageCode[]>([])

  const [openForm, setOpenForm] = useState<boolean>(false)
  const [editing, setEditing] = useState<LandingPageCode | null>(null)
  const [saving, setSaving] = useState<boolean>(false)

  const [pendingDelete, setPendingDelete] = useState<LandingPageCode | null>(null)
  const [deleting, setDeleting] = useState<boolean>(false)

  const fetchRows = async () => {
    setLoading(true)
    const { data, error } = await getLandingPageCodes()
    if (error) toastError(extractErrorMessage(error, t))
    setRows(data ?? [])
    setLoading(false)
  }

  useEffect(() => {
    if (flagOn) fetchRows()
  }, [flagOn])

  if (!flagOn) return <Navigate to="/" replace />

  const openCreate = () => {
    setEditing(null)
    setOpenForm(true)
  }

  const openEdit = (row: LandingPageCode) => {
    setEditing(row)
    setOpenForm(true)
  }

  const closeForm = () => {
    setOpenForm(false)
    setEditing(null)
  }

  const handleSubmit = async (form: FormState) => {
    setSaving(true)
    const { error } = editing
      ? await updateLandingPageCode({
          id: editing.id,
          attributes: {
            code: form.code,
            url: form.url,
            notes: form.notes || null,
            is_default: form.isDefault,
          },
        })
      : await createLandingPageCode({
          code: form.code,
          url: form.url,
          notes: form.notes || null,
          isDefault: form.isDefault,
        })
    setSaving(false)

    if (error) {
      toastError(extractErrorMessage(error, t))
      return
    }
    toastSuccess(
      editing ? t('dashboardLandingPages.success.updated') : t('dashboardLandingPages.success.created')
    )
    closeForm()
    fetchRows()
  }

  const onConfirmDelete = async () => {
    if (!pendingDelete) return
    setDeleting(true)
    const { error } = await deleteLandingPageCode({ id: pendingDelete.id })
    setDeleting(false)
    if (error) {
      toastError(extractErrorMessage(error, t))
      return
    }
    toastSuccess(t('dashboardLandingPages.success.deleted'))
    setPendingDelete(null)
    fetchRows()
  }

  const initialForm: FormState = editing
    ? {
        code: editing.code,
        url: editing.url,
        notes: editing.notes ?? '',
        isDefault: editing.is_default,
      }
    : emptyForm

  return (
    <>
      <ResponsiveDialog
        isOpen={openForm}
        setIsOpen={(v) => (v ? setOpenForm(true) : closeForm())}
        title={
          editing ? t('dashboardLandingPages.modal.editTitle') : t('dashboardLandingPages.modal.createTitle')
        }
      >
        <LandingPageForm
          key={editing?.id ?? 'new'}
          initial={initialForm}
          saving={saving}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      </ResponsiveDialog>

      <Dialog open={!!pendingDelete} handler={() => setPendingDelete(null)} size="xs">
        <DialogHeader>{t('dashboardLandingPages.delete.title')}</DialogHeader>
        <DialogBody>
          {t('dashboardLandingPages.delete.confirm')} <strong>{pendingDelete?.code}</strong>?
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button size="sm" variant="outlined" onClick={() => setPendingDelete(null)} disabled={deleting}>
            {t('dashboardLandingPages.cancel')}
          </Button>
          <Button
            size="sm"
            color="red"
            onClick={onConfirmDelete}
            disabled={deleting}
            data-testid="landing-page-confirm-delete-btn"
          >
            {t('dashboardLandingPages.delete.confirmAction')}
          </Button>
        </DialogFooter>
      </Dialog>

      <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-start gap-4 py-4">
        <div className="flex flex-col lg:max-w-[60%]">
          <Typography variant="h1" className="text-2xl md:text-5xl">
            {t('dashboardLandingPages.title')}
          </Typography>
          <Typography className="dark:text-gray-200">{t('dashboardLandingPages.description')}</Typography>
        </div>

        <div className="flex items-center space-x-4">
          <IconButton
            size="sm"
            className="rounded-full"
            onClick={openCreate}
            data-testid="create-landing-page-btn"
          >
            <AiOutlinePlus className="text-[20px]" />
          </IconButton>
          <Typography className="font-bold dark:text-gray-200">
            {t('dashboardLandingPages.create')}
          </Typography>
        </div>
      </div>

      {loading && <Spinner className="h-6 w-6" />}

      {!loading && rows.length === 0 && (
        <div className="w-full text-center">
          <NoUploads>
            <Typography className="dark:text-gray-200">{t('dashboardLandingPages.empty')}</Typography>
          </NoUploads>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
          {rows.map((row) => (
            <Card
              key={row.id}
              className="w-full overflow-hidden cursor-pointer hover:bg-base-gray-400"
              onClick={() => openEdit(row)}
              data-testclass="landing-page-preview-card"
              data-code={row.code}
              data-default={row.is_default}
            >
              <CardBody className="min-w-0">
                <div className="flex space-x-2 justify-between items-start">
                  <div className="flex flex-col flex-1 gap-2 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <Typography
                        className="font-bold truncate"
                        title={row.code}
                        data-testclass="landing-page-preview-card-code"
                      >
                        {row.code}
                      </Typography>
                      {row.is_default && (
                        <Chip
                          size="sm"
                          color="amber"
                          value={t('dashboardLandingPages.defaultBadge')}
                          className="shrink-0"
                        />
                      )}
                    </div>

                    {row.notes && (
                      <Typography variant="small" className="opacity-80 whitespace-pre-wrap break-words">
                        {row.notes}
                      </Typography>
                    )}

                    <Typography variant="small" className="truncate" title={row.url}>
                      {t('dashboardLandingPages.fields.url')}{' '}
                      <a
                        href={row.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="font-bold text-black dark:text-base-gray-400 underline"
                      >
                        {row.url}
                      </a>
                    </Typography>

                    <Typography variant="small">
                      {t('dashboard.lastUpdate')}{' '}
                      <strong className="text-black dark:text-base-gray-400">
                        {row.updated_at ? formatDateString(row.updated_at) : '-'}
                      </strong>
                    </Typography>
                  </div>

                  <IconButton
                    variant="text"
                    size="sm"
                    color="yellow"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setPendingDelete(row)
                    }}
                    aria-label={t('dashboardLandingPages.actions.delete')}
                    data-testid="landing-page-delete-btn"
                  >
                    <PiTrash className="text-primary dark:text-base-gray-500 text-[20px]" />
                  </IconButton>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </>
  )
}

export default DashboardLandingPages
